import React, { useState, useEffect } from 'react';
import { authFetch } from '../../Utilities/Api';
import { toast } from 'react-toastify';
import EditProfile from '../Profile/EditProfile';
import Security from './Security';
import './Settings.css';

const Settings = () => {
    const [activeTab, setActiveTab] = useState('account');
    const [loading, setLoading] = useState(true);

    // ✅ State keys now match User Model privacySettings
    const [privacy, setPrivacy] = useState({
        showEmail: false,
        showLocation: false,
        showBirthdate: false,
        showJoinDate: false,
        appearOnline: true,
        allowStrangerDMs: true
    });

    const [blockedUsers, setBlockedUsers] = useState([]);
    const [loadingBlocked, setLoadingBlocked] = useState(false);

    // 📥 Initial Load: Fetch Profile & Privacy
    useEffect(() => {
        const loadSettings = async () => {
            try {
                const res = await authFetch('/users/profile');
                const data = await res.json();
                if (res.ok && data.privacySettings) {
                    setPrivacy(prev => ({
                        ...prev,
                        ...data.privacySettings
                    }));
                }
            } catch (err) {
                console.error("❌ [Settings] Load Error:", err);
            } finally {
                setLoading(false);
            }
        };
        loadSettings();
    }, []);

    // Load Blocked Users when tab switches
    useEffect(() => {
        if (activeTab === 'blocked') {
            fetchBlockedUsers();
        }
    }, [activeTab]);

    const fetchBlockedUsers = async () => {
        setLoadingBlocked(true);
        try {
            const res = await authFetch('/users/blocked');
            const data = await res.json();
            if (res.ok) setBlockedUsers(data);
        } catch (err) {
            console.error(err);
            toast.error("Failed to load blocked users");
        } finally {
            setLoadingBlocked(false);
        }
    };

    // ✅ Toggle Logic: Communicates with userController.updateSettings
    const handleToggle = async (field) => {
        const newValue = !privacy[field];

        // Optimistic UI Update
        setPrivacy(prev => ({ ...prev, [field]: newValue }));

        try {
            const res = await authFetch('/users/settings', {
                method: 'PATCH', // Matches standardized routes
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    privacySettings: { [field]: newValue }
                })
            });

            if (!res.ok) throw new Error("Update failed");

            const friendlyName = field.replace(/([A-Z])/g, ' $1').toLowerCase();
            toast.success(`Updated ${friendlyName}`);
        } catch (err) {
            toast.error("Failed to save setting");
            // Revert state if server fails
            setPrivacy(prev => ({ ...prev, [field]: !newValue }));
        }
    };

    const handleUnblock = async (userId) => {
        try {
            const res = await authFetch(`/users/unblock/${userId}`, { method: 'PUT' });
            if (res.ok) {
                toast.success("User unblocked");
                setBlockedUsers(prev => prev.filter(u => u._id !== userId));
            }
        } catch (err) {
            toast.error("Error unblocking user");
        }
    };

    if (loading) return <div className="settings-loading">Loading settings...</div>;

    return (
        <div className="settings-page">
            <h1 className="settings-title">Settings</h1>

            <div className="settings-layout">
                {/* SIDEBAR */}
                <div className="settings-sidebar">
                    <button
                        onClick={() => setActiveTab('account')}
                        className={`settings-tab ${activeTab === 'account' ? 'active' : ''}`}
                    >
                        <span className="tab-icon">✏️</span> Edit Profile
                    </button>

                    <button
                        onClick={() => setActiveTab('security')}
                        className={`settings-tab ${activeTab === 'security' ? 'active' : ''}`}
                    >
                        <span className="tab-icon">🔐</span> Security
                    </button>

                    <button
                        onClick={() => setActiveTab('general')}
                        className={`settings-tab ${activeTab === 'general' ? 'active' : ''}`}
                    >
                        <span className="tab-icon">🛡️</span> Privacy Visibility
                    </button>

                    <button
                        onClick={() => setActiveTab('blocked')}
                        className={`settings-tab ${activeTab === 'blocked' ? 'active' : ''}`}
                    >
                        <span className="tab-icon">🚫</span> Blocked Users
                    </button>
                </div>

                {/* CONTENT */}
                <div className="settings-content">
                    {activeTab === 'account' && (
                        <div className="fade-in">
                            <div className="settings-card">
                                <h2>Edit Profile</h2>
                                <EditProfile />
                            </div>
                        </div>
                    )}

                    {activeTab === 'security' && <Security />}

                    {activeTab === 'general' && (
                        <div className="fade-in">
                            <div className="settings-card">
                                <h2>Privacy Visibility</h2>
                                <p className="card-desc">
                                    Manage how you appear to others and who can contact you.
                                </p>

                                <div className="toggles-list">
                                    <ToggleRow
                                        label="Appear Online"
                                        desc="Show your online status to others"
                                        isOn={privacy.appearOnline}
                                        onToggle={() => handleToggle('appearOnline')}
                                    />
                                    <ToggleRow
                                        label="Accept Stranger DMs"
                                        desc="Allow people who aren't your friends to start chats"
                                        isOn={privacy.allowStrangerDMs}
                                        onToggle={() => handleToggle('allowStrangerDMs')}
                                    />
                                    <hr className="settings-divider" />
                                    <ToggleRow
                                        label="Show Email"
                                        desc="Display email on your public profile"
                                        isOn={privacy.showEmail}
                                        onToggle={() => handleToggle('showEmail')}
                                    />
                                    <ToggleRow
                                        label="Show Location"
                                        desc="Display your city/country"
                                        isOn={privacy.showLocation}
                                        onToggle={() => handleToggle('showLocation')}
                                    />
                                    <ToggleRow
                                        label="Show Birthday"
                                        desc="Display your birth date"
                                        isOn={privacy.showBirthdate}
                                        onToggle={() => handleToggle('showBirthdate')}
                                    />
                                    <ToggleRow
                                        label="Show Join Date"
                                        desc="Show when you joined the platform"
                                        isOn={privacy.showJoinDate}
                                        onToggle={() => handleToggle('showJoinDate')}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'blocked' && (
                        <div className="settings-card fade-in">
                            <h2 className="text-danger">Blocked Users</h2>
                            {loadingBlocked ? (
                                <p className="text-muted">Loading...</p>
                            ) : blockedUsers.length === 0 ? (
                                <div className="empty-state"><p>No blocked users.</p></div>
                            ) : (
                                <ul className="blocked-list">
                                    {blockedUsers.map(user => (
                                        <li key={user._id} className="blocked-item">
                                            <div className="blocked-info">
                                                <img
                                                    src={user.profilePicture || "https://via.placeholder.com/40"}
                                                    alt={user.username}
                                                    className="blocked-avatar"
                                                />
                                                <div style={{ marginLeft: '10px' }}>
                                                    <p className="blocked-name">{user.nickname || user.username}</p>
                                                    <p className="blocked-handle">@{user.username}</p>
                                                </div>
                                            </div>
                                            <button onClick={() => handleUnblock(user._id)} className="unblock-btn">Unblock</button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const ToggleRow = ({ label, desc, isOn, onToggle }) => (
    <div className="toggle-row">
        <div className="toggle-info">
            <h4>{label}</h4>
            <p>{desc}</p>
        </div>
        <div
            className={`toggle-switch ${isOn ? 'on' : 'off'}`}
            onClick={onToggle}
            role="button"
            aria-pressed={isOn}
        >
            <div className="toggle-slider"></div>
        </div>
    </div>
);

export default Settings;